import {
  Paper,
  Stack,
  Box,
  Select,
  Typography,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ButtonBase,
  Menu,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Tooltip,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import DemandCapacityGraph from "../components/DemandCapacityGraph";
import { useNavigate } from "react-router-dom";
import ltcpRoutes from "../constants/ltcpRoutes";
import TuneIcon from "@mui/icons-material/Tune";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useContext, useEffect, useState, useRef } from "react";
import { LTCPContext } from "../LTCPContext";
import {
  getCuData,
  getCuDataL1,
  getCuDataL2,
  getCuFilters,
} from "../api/ltcpBackend";
import ExpandIcon from "LTCP Module/components/ExpandIcon";
import CollapseIcon from "LTCP Module/components/CollapseIcon";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MultiSearchFilter from "LTCP Module/components/MultiSearchFilter";
import {
  branchFilterValueConvert,
  monthFilterValueConvert,
} from "utils/filterValueConverter";
import { useMsal } from "@azure/msal-react";
import { roundOffNumber } from "utils/roundOff";
import { OverviewButton } from "./DashboardPage";

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Use the common round off function with error handling feature
// to avoid error for the values where null are expected.
function roundNumber(val) {
  try {
    return roundOffNumber(val);
  } catch {
    return val;
  }
}

// This is a stateless table cell component that will create a table
// cell with the given value and based on the predefined threshold value
// it will fill the cell with proper background color
function ColorCell({ val }) {
  let color = "#ffec9a"; // amber

  if (val > 90) color = "#ff9998"; // red
  else if (val < 80) color = "#a3e1d2"; // green

  try {
    return (
      <TableCell
        sx={{
          background: color,
          textAlign: "center",
          textWrap: "nowrap",
          px: 2,
        }}
        style={{ "mso-number-format": "'\\@'" }}
      >
        {roundNumber(val) + "%"}
      </TableCell>
    );
  } catch {
    return (
      <TableCell
        sx={{
          background: color,
          textAlign: "right",
          textWrap: "nowrap",
          px: 2,
        }}
        style={{ "mso-number-format": "'\\@'" }}
      >
        {val + "%"}
      </TableCell>
    );
  }
}

// This is the table row component used for the detailed cu data table.
// This component contain the top level row as well as the expand and collapse
// logics as well as the nested rows in it.
function NestedRow({
  header,
  item,
  rowIndex,
  nestedToggler,
  bgState,
  subcategoryState,
  branchState,
  brandState,
  formatState,
  basepackState,
  factoryState,
  yearState,
  quarterState,
  monthState,
  timeGranularity,
  cuConstrain,
  month_18,
}) {
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount.username;

  const [nestedData, setNestedData] = useState([]);
  const { demandFile, setLoading, runtime, runtype } = useContext(LTCPContext);

  function toggleClass() {
    let elmt = document.getElementById("cu-table-btn-" + rowIndex);
    if (elmt) {
      if (elmt.classList.contains("ltcp-row-expand"))
        elmt.classList.remove("ltcp-row-expand");
      else elmt.classList.add("ltcp-row-expand");
    }
  }

  async function fetchCuDataL2() {
    setLoading(true);

    let formatFilter = "";
    let basepackFilter = "";

    if (nestedToggler.includes("format")) {
      formatFilter = item.level;
      basepackFilter = basepackState;
    } else {
      formatFilter = formatState;
      basepackFilter = item.level;
    }

    let variant = "basepack";
    if (nestedToggler.includes("factory")) variant = "factory";

    const data = await getCuDataL2(userEmail, {
      demandFile,
      bgState,
      subcategoryState,
      branchState: branchFilterValueConvert(branchState),
      brandState,
      formatState: formatFilter,
      basepackState: basepackFilter,
      factoryState,
      yearState,
      quarterState,
      monthState,
      timeGranularity,
      variant,
      runtime,
      runtype,
      month_18,
    });

    toggleClass();
    setNestedData(data);
    setLoading(false);
  }

  function collapseRows() {
    setNestedData([]);
    toggleClass();
  }

  return (
    <>
      <TableRow>
        <TableCell sx={{ textWrap: "nowrap", borderBottom: "1px solid #ccc" }}>
          <ButtonBase
            id={"cu-table-btn-" + rowIndex}
            onClick={() => {
              if (nestedData.length === 0) {
                fetchCuDataL2();
              } else {
                collapseRows();
              }
            }}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              textWrap: "nowrap",
              gap: 2,
            }}
          >
            {nestedData.length === 0 ? <ExpandIcon /> : <CollapseIcon />}
            {item.level}
          </ButtonBase>
        </TableCell>
        {header.map((date, index) => (
          <ColorCell key={index} val={item[date]} />
        ))}
      </TableRow>
      {nestedData
        .filter((item) =>
          cuConstrain === "pack_constrained"
            ? item.type === "cu_const"
            : item.type === "cu_unconst"
        )
        .map((nitem, index) => (
          <TableRow key={index}>
            <TableCell
              sx={{
                pl: 6,
                textWrap: "nowrap",
                borderBottom: "1px solid #ccc",
                background: "#E8F3F8",
              }}
            >
              {nitem.level}
            </TableCell>
            {header.map((date, index) => (
              <ColorCell key={index} val={nitem[date]} />
            ))}
          </TableRow>
        ))}
    </>
  );
}

// Helper function to get the remaining months of the current year from
// the current month as mmm format in string data type.
function getRemainingMonths() {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const currentMonthIndex = new Date().getMonth();

  return monthNames.slice(currentMonthIndex).join(",");
}

// This is the capacity utilization page where we are collecting the cu
// data on this page load based on the default filters and on any filter
// change it will refect the data from the server and will refresh the
// table and graph.
function CUPage() {
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount.username;

  const {
    demandFile,
    runtime,
    runtype,
    cuFilters,
    setLoading,
    thresholdLevel,
    commonSelectedFilters,
    setCommonSelectedFilters,
    cuSelectedFilters,
    setCuSelectedFilters,
  } = useContext(LTCPContext);
  const [filterDropdown, setFilterDropdown] = useState(null);
  const [currentFilters, setCurrentFilters] = useState(cuFilters);
  const {
    cuConstrain,
  } = cuSelectedFilters;

  const {
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    appliedFilters,
    load_ym,
  } = commonSelectedFilters;
  const timeGranularity = commonSelectedFilters.timeGranularity || "MoM";
  const setCommonSelectedFilter = (key, value) =>
    setCommonSelectedFilters((prev) => ({ ...prev, [key]: value }));
  const setCuSelectedFilter = (key, value) =>
    setCuSelectedFilters((prev) => ({ ...prev, [key]: value }));
  const setCuConstrain = (value) => setCuSelectedFilter("cuConstrain", value);
  const setTimeGranularity = (value) =>
    setCommonSelectedFilter("timeGranularity", value);
  const setAppliedFilters = (value) =>
    setCommonSelectedFilter("appliedFilters", value);
  const setBgState = (value) => setCommonSelectedFilter("bgState", value);
  const setSubcategoryState = (value) =>
    setCommonSelectedFilter("subcategoryState", value);
  const setBranchState = (value) => setCommonSelectedFilter("branchState", value);
  const setBrandState = (value) => setCommonSelectedFilter("brandState", value);
  const setFormatState = (value) => setCommonSelectedFilter("formatState", value);
  const setBasepackState = (value) =>
    setCommonSelectedFilter("basepackState", value);
  const setFactoryState = (value) => setCommonSelectedFilter("factoryState", value);
  const setYearState = (value) => setCommonSelectedFilter("yearState", value);
  const setQuarterState = (value) => setCommonSelectedFilter("quarterState", value);
  const setMonthState = (value) => setCommonSelectedFilter("monthState", value);
  const setLoad18Months = (value) =>
    setCommonSelectedFilter("load_ym", value);
  const [nestedToggler, setNestedToggler] = useState([]);
  const [rowToggleState, setRowToggleState] = useState(false);
  const [result, setResult] = useState({
    date: [],
    demand: [],
    pack_cap_tpm: [],
    pack_cap_tpm_new: [],
    cu_const: [],
    cu_unconst: [],
  });
  const [nestedData, setNestedData] = useState({
    header: [],
    data: [],
  });

  useEffect(() => {
    setRowToggleState(false);
  }, [nestedToggler, timeGranularity]);

  function clearFilterHandler() {
    setBgState("All");
    setSubcategoryState("All");
    setBranchState("All");
    setBrandState("All");
    setFormatState("All");
    setBasepackState("All");
    setFactoryState("All");
    setAppliedFilters([]);
    setFilterDropdown(null);
    setLoad18Months(false);
  }

  function toggleAllRows() {
    nestedData.data
      .filter((item) =>
        cuConstrain === "pack_constrained"
          ? item.type === "cu_const"
          : item.type === "cu_unconst"
      )
      .forEach((_, i) => {
        let elmt = document.getElementById("cu-table-btn-" + i);

        if (elmt) {
          if (rowToggleState) {
            if (elmt.classList.contains("ltcp-row-expand")) elmt.click();
          } else {
            if (!elmt.classList.contains("ltcp-row-expand")) elmt.click();
          }
        }
      });
    setRowToggleState(!rowToggleState);
  }

  function handleNestedTableToggler(e, values) {
    setNestedToggler(values);
  }

  // const exportToExcel = () => {
  //   const table = tableRef.current;
  //   const worksheet = XLSX.utils.table_to_sheet(table); // Convert table to sheet
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  //   const excelBuffer = XLSX.write(workbook, {
  //     bookType: "xlsx",
  //     type: "array",
  //   });
  //   const dataBlob = new Blob([excelBuffer], {
  //     type: "application/octet-stream",
  //   });

  //   saveAs(dataBlob, "cu-data.xlsx");
  // };

  const exportToExcel = () => {
    const table = tableRef.current;
    const worksheet = XLSX.utils.table_to_sheet(table); // Convert table to sheet

    // Modify worksheet values: multiply by 100 except first row and first column
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      // Start from row 1 (skip header)
      for (let C = range.s.c + 1; C <= range.e.c; ++C) {
        // Start from column 1 (skip first col)
        const cellAddress = { r: R, c: C };
        const cellRef = XLSX.utils.encode_cell(cellAddress);
        const cell = worksheet[cellRef];

        if (cell && typeof cell.v === "number") {
          cell.v = cell.v * 100; // Multiply numeric value by 100
          cell.w = undefined; // Clear formatted text so Excel re-renders it
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(dataBlob, "cu-data.xlsx");
  };

  async function fetchCuData() {
    const resp = await Promise.all([
      getCuData(userEmail, {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        brandState,
        formatState,
        basepackState,
        factoryState,
        yearState,
        quarterState,
        monthState: monthFilterValueConvert(monthState),
        timeGranularity,
        runtime,
        runtype,
        month_18: timeGranularity === "YoY" ? false : (yearState === "All" ? load_ym : false),
      }),
      getCuFilters(userEmail, {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        brandState,
        formatState,
        basepackState,
        factoryState,
        runtime,
        runtype,
      }),
    ]);

    const data = resp[0];
    if (resp[1] === 401) return navigate(ltcpRoutes.HOME);
    const filters = resp[1];

    appliedFilters.forEach((filter) => {
      filters[filter] = currentFilters[filter];
    });

    setResult(data);
    setCurrentFilters(filters);
  }

  async function fetchNestedData() {
    let variant = "basepack";
    if (nestedToggler.includes("format")) variant = "format";

    const data = await getCuDataL1(userEmail, {
      demandFile,
      bgState,
      subcategoryState,
      branchState: branchFilterValueConvert(branchState),
      brandState,
      formatState,
      basepackState,
      factoryState,
      yearState,
      quarterState,
      monthState: monthFilterValueConvert(monthState),
      timeGranularity,
      variant,
      runtime,
      runtype,
      month_18: load_ym,
    });

    setNestedData(data);
  }

  useEffect(() => {
    if (cuFilters.bg.length === 0) return navigate(ltcpRoutes.LANDING);

    async function dataLoader() {
      setLoading(true);
      if (nestedToggler.length === 2) {
        await Promise.all([fetchNestedData(), fetchCuData()]);
      } else {
        await fetchCuData();
      }
      setLoading(false);
    }

    dataLoader();
  }, [
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    nestedToggler,
    load_ym
  ]);

  return (
    <Stack p={4} py={2} gap={2} flexGrow={1} overflow="auto">
      <Stack direction="row" gap={2} alignItems="center">
        <OverviewButton
            label="Overview"
            colorFrom="#2563eb"
            colorTo="#1f36c7"
            onClick={() => navigate(ltcpRoutes.DASHBOARD)}
          />
        <OverviewButton
          label="Demand"
          colorFrom="#855FC0"
          colorTo="#5346BA"
          onClick={() => navigate(ltcpRoutes.DEMAND)}
        />
        <OverviewButton
          label="Capacity"
          colorFrom="#46C5F2"
          colorTo="#6692D9"
          onClick={() => navigate(ltcpRoutes.CAPACITY)}
        />
      </Stack>
      <Stack
        height={40}
        direction="row"
        alignItems="center"
        justifyContent="start"
        gap={2}
      >
        {/* <IconButton
          sx={{ color: "black" }}
          onClick={() => navigate(ltcpRoutes.DASHBOARD)}
        >
          <KeyboardBackspaceIcon />
        </IconButton> */}
        <Typography fontWeight="bold" fontSize={21}>
          Capacity Utilization
        </Typography>
      </Stack>
      <Paper sx={{ height: 44 }}>
        <Stack
          px={2}
          direction="row"
          bgcolor="white"
          borderRadius="3px"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Stack px={3} gap={2} py={1} width="100%" direction="row">
            <Box display="flex" flexDirection="row" alignItems="center">
              <ButtonBase
                onClick={(e) => setFilterDropdown(e.currentTarget)}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <TuneIcon fontSize="small" />
                <Typography ml={1} fontSize={12}>
                  Filter
                </Typography>
                <KeyboardArrowDownIcon fontSize="small" />
              </ButtonBase>
              <Menu
                open={Boolean(filterDropdown)}
                anchorEl={filterDropdown}
                onClose={() => setFilterDropdown(null)}
              >
                <Stack p={1} gap={1} width={150}>
                  <Stack>
                    <Typography fontSize={12}>BG</Typography>
                    <MultiSearchFilter
                      value={bgState}
                      setValue={setBgState}
                      options={currentFilters.bg}
                      filterName="bg"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack>
                    <Typography fontSize={12}>Sub-Category</Typography>
                    <MultiSearchFilter
                      removeUnderscore
                      value={subcategoryState}
                      setValue={setSubcategoryState}
                      options={currentFilters.subcategory}
                      filterName="subcategory"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack>
                    <Typography fontSize={12}>Branch</Typography>
                    <MultiSearchFilter
                      value={branchState}
                      setValue={setBranchState}
                      options={currentFilters.branch}
                      filterName="branch"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack>
                    <Typography fontSize={12}>Brand</Typography>
                    <MultiSearchFilter
                      value={brandState}
                      setValue={setBrandState}
                      options={currentFilters.brand}
                      filterName="brand"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack>
                    <Typography fontSize={12}>Format L2</Typography>
                    <MultiSearchFilter
                      value={formatState}
                      setValue={setFormatState}
                      options={currentFilters.format}
                      filterName="format"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack>
                    <Typography fontSize={12}>Basepack</Typography>
                    <MultiSearchFilter
                      value={basepackState}
                      setValue={setBasepackState}
                      options={currentFilters.basepack}
                      filterName="basepack"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack>
                    <Typography fontSize={12}>Factory</Typography>
                    <MultiSearchFilter
                      value={factoryState}
                      setValue={setFactoryState}
                      options={currentFilters.factory}
                      filterName="factory"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack direction="row" justifyContent="end">
                    <Button variant="text" onClick={clearFilterHandler}>
                      Clear
                    </Button>
                  </Stack>
                </Stack>
              </Menu>
            </Box>
            <Box gap={1} display="flex" flexDirection="row" alignItems="center">
              <Typography fontSize={12}>Year</Typography>
              <MultiSearchFilter
                width={100}
                options={currentFilters.year}
                value={yearState}
                setValue={(v) => {
                  setLoad18Months(false);
                  setYearState(v);
                }}
                filterName="year"
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
              />
            </Box>
            <Box gap={1} display="flex" flexDirection="row" alignItems="center">
              <Typography fontSize={12}>Quarter</Typography>
              <MultiSearchFilter
                width={100}
                value={quarterState}
                setValue={(v) => {
                  setLoad18Months(false);
                  setQuarterState(v);
                }}
                options={["Q1", "Q2", "Q3", "Q4"]}
                disabled={timeGranularity !== "QoQ"}
                filterName="quarter"
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
              />
            </Box>
            <Box gap={1} display="flex" flexDirection="row" alignItems="center">
              <Typography fontSize={12}>Month</Typography>
              <MultiSearchFilter
                width={100}
                value={monthState}
                setValue={(v) => {
                  setLoad18Months(false);
                  setMonthState(v);
                }}
                options={[
                  "Jan",
                  "Feb",
                  "Mar",
                  "Apr",
                  "May",
                  "Jun",
                  "Jul",
                  "Aug",
                  "Sep",
                  "Oct",
                  "Nov",
                  "Dec",
                ]}
                disabled={timeGranularity !== "MoM"}
                filterName="month"
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
              />
            </Box>
            <Stack gap={1} direction="row" ml="auto">
              <Typography fontSize={12}>CU% :</Typography>
              <Select
                size="small"
                sx={{ height: 28 }}
                value={cuConstrain}
                onChange={(e) => setCuConstrain(e.target.value)}
              >
                <MenuItem value="pack_unconstrained">
                  Pack Unconstrained
                </MenuItem>
                <MenuItem value="pack_constrained">Pack Constrained</MenuItem>
              </Select>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
      <Paper
        sx={{
          p: 1,
          display: "flex",
          flexDirection: "column",
          height: "calc(45vh - 128px)",
          minHeight: "calc(45vh - 128px)",
          maxHeight: "calc(45vh - 128px)",
        }}
      >
        <Stack width="100%" direction="row" justifyContent="end">
          <ToggleButtonGroup
            value={timeGranularity}
            onChange={(_, v) => {
              if (v === null) return;

              setTimeGranularity(v);
              if (v === "YoY") {
                setMonthState("All");
                setQuarterState("All");
              } else if (v === "QoQ") {
                setMonthState("All");
              } else if (v === "MoM") {
                setQuarterState("All");
                setMonthState("All");
              }
            }}
            exclusive
          >
            <ToggleButton value="YoY">YoY</ToggleButton>
            <ToggleButton value="QoQ">QoQ</ToggleButton>
            <ToggleButton value="MoM">MoM</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Box sx={{ flexGrow: 1, display: "flex" }}>
          <DemandCapacityGraph
            threshold={thresholdLevel[subcategoryState]}
            demand={result.demand.map((item) => roundOffNumber(item))}
            capacity={
              cuConstrain === "pack_constrained"
                ? result.pack_cap_tpm_new.map((item) => roundOffNumber(item))
                : result.pack_cap_tpm.map((item) => roundOffNumber(item))
            }
            cu={
              cuConstrain === "pack_constrained"
                ? result.cu_const.map((item) => roundOffNumber(item))
                : result.cu_unconst.map((item) => roundOffNumber(item))
            }
            labels={result.date.map((item) => {
              if (timeGranularity === "MoM") {
                try {
                  let month = item.split("-");
                  month =
                    monthNames[Number.parseInt(month[1]) - 1] + "-" + month[0];

                  return month;
                } catch (e) {
                  console.error(e);
                  return item;
                }
              } else {
                return item;
              }
            })}
            labelBg={true}
          />
        </Box>
      </Paper>
      <Paper
        sx={{
          p: 1,
          height: "calc(55vh - 128px)",
          minHeight: "calc(55vh - 128px)",
          maxHeight: "calc(55vh - 128px)",
        }}
      >
        <Stack
          height="30px"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <ToggleButtonGroup
            size="small"
            sx={{ mb: 1 }}
            value={nestedToggler}
            onChange={handleNestedTableToggler}
          >
            <ToggleButton
              value="format"
              disabled={
                nestedToggler.includes("factory") &&
                nestedToggler.includes("basepack")
              }
            >
              Format L2
            </ToggleButton>
            <ToggleButton
              value="basepack"
              disabled={
                nestedToggler.includes("format") &&
                nestedToggler.includes("factory")
              }
            >
              Basepack
            </ToggleButton>
            <ToggleButton
              value="factory"
              disabled={
                nestedToggler.includes("format") &&
                nestedToggler.includes("basepack")
              }
            >
              Factory
            </ToggleButton>
            <Typography
              variant="body1"
              sx={{
                color: "blue",
                textDecoration: "underline",
                paddingLeft:2,
                fontStyle: "italic"
              }}>
              Select any two
            </Typography>
          </ToggleButtonGroup>

          {nestedToggler.length > 1 && (
            <Stack direction="row" alignItems="center" gap={1}>
              <Tooltip
                title={rowToggleState ? "Collapse rows" : "Expand rows"}
                placement="top"
              >
                <IconButton onClick={toggleAllRows} size="small">
                  {rowToggleState ? (
                    <CloseFullscreenIcon fontSize="small" />
                  ) : (
                    <OpenInFullIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Download data" placement="top">
                <IconButton onClick={exportToExcel} size="small">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
        <Box sx={{ display: "flex", height: "calc(100% - 50px)" }}>
          <TableContainer>
            {nestedToggler.length < 2 ? (
              <Table size="small">
                <TableHead sx={{ background: "#354acd" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        textWrap: "nowrap",
                        fontWeight: "bold",
                        color: "#FFFFFF",
                      }}
                    >
                      Element
                    </TableCell>
                    {result.date.map((item, index) => {
                      let month_value = item;

                      if (timeGranularity === "MoM") {
                        try {
                          let month = item.split("-");
                          month_value =
                            monthNames[Number.parseInt(month[1]) - 1] +
                            "-" +
                            month[0];
                        } catch (e) {
                          console.error(e);
                          month_value = item;
                        }
                      }

                      return (
                        <TableCell
                          key={index}
                          sx={{
                            textWrap: "nowrap",
                            textAlign: "center",
                            fontWeight: "bold",
                            color: "#FFFFFF",
                          }}
                        >
                          {month_value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        borderBottom: "1px solid #ccc",
                        textWrap: "nowrap",
                      }}
                    >
                      Demand (T)
                    </TableCell>
                    {result.demand.map((item, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          textAlign: "right",
                          borderBottom: "1px solid #ccc",
                          textWrap: "nowrap",
                          px: 2,
                        }}
                      >
                        {roundNumber(item)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        borderBottom: "1px solid #ccc",
                        textWrap: "nowrap",
                      }}
                    >
                      Capacity (T)
                    </TableCell>
                    {(cuConstrain === "pack_constrained"
                      ? result.pack_cap_tpm_new
                      : result.pack_cap_tpm
                    ).map((item, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          textAlign: "right",
                          borderBottom: "1px solid #ccc",
                          textWrap: "nowrap",
                          px: 2,
                        }}
                      >
                        {roundNumber(item)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>CU%</TableCell>
                    {(cuConstrain === "pack_constrained"
                      ? result.cu_const
                      : result.cu_unconst
                    ).map((item, index) => (
                      <ColorCell key={index} val={item} />
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Table stickyHeader size="small" ref={tableRef}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        textWrap: "nowrap",
                        fontWeight: "bold",
                        background: "#C5EAFB",
                      }}
                    >
                      {nestedToggler.includes("format") ? "Format" : "Basepack"}
                    </TableCell>
                    {nestedData.header.map((item, index) => {
                      let month_value = item;

                      if (timeGranularity === "MoM") {
                        let month = item.split("-");
                        month_value =
                          monthNames[Number.parseInt(month[1]) - 1] +
                          "-" +
                          month[0];
                      }

                      return (
                        <TableCell
                          key={index}
                          sx={{
                            textWrap: "nowrap",
                            textAlign: "center",
                            fontWeight: "bold",
                            background: "#C5EAFB",
                          }}
                        >
                          {month_value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nestedData.data
                    .filter((item) =>
                      cuConstrain === "pack_constrained"
                        ? item.type === "cu_const"
                        : item.type === "cu_unconst"
                    )
                    .map((item, index) => (
                      <NestedRow
                        key={index + timeGranularity}
                        item={item}
                        rowIndex={index}
                        header={nestedData.header}
                        bgState={bgState}
                        subcategoryState={subcategoryState}
                        branchState={branchState}
                        brandState={brandState}
                        formatState={formatState}
                        basepackState={basepackState}
                        factoryState={factoryState}
                        yearState={yearState}
                        quarterState={quarterState}
                        monthState={monthFilterValueConvert(monthState)}
                        timeGranularity={timeGranularity}
                        nestedToggler={nestedToggler}
                        cuConstrain={cuConstrain}
                      />
                    ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Box>
        <Stack
          mt={1}
          gap={1}
          height="20px"
          direction="row"
          alignItems="center"
          justifyContent="end"
        >
          <Box sx={{ width: 12, height: 6, bgcolor: "#ff9998" }}></Box>
          <Typography fontSize={12}>{"CU > Threshold"}</Typography>
          <Box sx={{ width: 12, height: 6, bgcolor: "#ffec9a" }}></Box>
          <Typography fontSize={12}>{"80<CU<Threshold"}</Typography>
          <Box sx={{ width: 12, height: 6, bgcolor: "#a3e1d2" }}></Box>
          <Typography fontSize={12}>{"CU < 80"}</Typography>
        </Stack>
      </Paper>
    </Stack>
  );
}

export default CUPage;
